import { useRouter } from "next/router";
import Script from "next/script";

const BUNDLE_LINK = process.env.NEXT_PUBLIC_BUNDLE_LINK;

export type AtlasSnippetProps = {
  appId: string;
  chatting?: boolean;
  enableChattingForVisitors?: boolean;
  recording?: boolean;
  baseUrl: string;
};

export default function AtlasSnippet({
  appId,
  chatting,
  enableChattingForVisitors,
  recording,
  baseUrl,
}: AtlasSnippetProps) {
  const router = useRouter();

  if (!chatting && !recording) return null;

  if (typeof window !== "undefined") {
    const onAtlasWidgetLinkClick = (
      props: {
        href: string;
        categorySlug?: string;
        articleSlug?: string;
        articleSection?: string;
      },
      event: Event
    ) => {
      if (props.categorySlug) {
        event.preventDefault();
        router.push(`${baseUrl}/categories/${props.categorySlug}`);
      } else if (props.articleSlug) {
        event.preventDefault();
        router.push(
          `${baseUrl}/articles/${props.articleSlug}${
            props.articleSection ? `#${props.articleSection}` : ""
          }`
        );
      }
    };

    const onAtlasIdentityChange = (identity: { isVisitor: boolean }) => {
      if (!chatting) return;

      const enableChat =
        enableChattingForVisitors || (identity && !identity?.isVisitor);

      window.Atlas?.call("update", {
        chat: {
          enabled: enableChat,
          openIncoming: enableChat,
          hideBubble: !enableChat,
        },
      });
    };

    Object.assign(window, { onAtlasWidgetLinkClick, onAtlasIdentityChange });
  }

  // widget SDK needs to run for Customer portal. Hiding the bubble if chat is turned off
  return (
    <Script id="script-atlas-snippet">{`
          (()=>{"use strict";const t={appId:"${appId}",v:2,autorun:{identityWaitTime:5, recording:{enabled:${!!recording}},chat:{enabled:false,openIncoming:false,hideBubble:true},callbacks:{onLinkClick:(props, event)=>window.onAtlasWidgetLinkClick(props,event),changeIdentity:(identity)=>window.onAtlasIdentityChange(identity)}},q:[],call:function(){this.q.push(arguments)},start:function(o){this.autorun=o||!0},identify:function(t){this.q.push(["identify",t])}};window.Atlas=t;const e=document.createElement("script");e.async=!0,e.src="${BUNDLE_LINK}";const s=document.getElementsByTagName("script")[0];s.parentNode?.insertBefore(e,s)})();
      `}</Script>
  );
}

// List is not exhaustive, only includes methods used in this file
type TAtlasQueueEntry =
  | [
      "update",
      (
        | {
            chat?: {
              enabled?: boolean;
              openIncoming?: boolean;
              hideBubble?: boolean;
            };
          }
        | undefined
      )
    ];

interface AtlasPublicCall {
  (...params: TAtlasQueueEntry): void;
}

declare global {
  interface Window {
    Atlas?: {
      call: AtlasPublicCall;
    };
  }
}
